import React, { useContext } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Text, Flex, Stack, Image, Button, useColorMode, useColorStyle } from '@tonic-ui/react';
import { useToggle } from '@tonic-ui/react-hooks';
import { useTranslation } from 'react-i18next';

import { getC1UserDetails } from '../../../src/store';
import { AuthContext } from '../../context/AuthContext';
import awslogo from '../../assets/images/aws_logo.svg';
import azurelogo from '../../assets/images/azure_logo.svg';
import gcplogo from '../../assets/images/gcp_logo.svg';
import ProfilingModal from './Questionnaire/profiling-modal';
import RouteLink from '../../components/route-link';
import { actionDispatcher } from '../../utils/ActionDispatchers';

const Onboarding = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const pageBG = {
    light: colorStyle.background.secondary,
    dark: colorStyle.background.primary,
  }[colorMode];
  const tileBG = {
    light: 'white',
    dark: colorStyle.background.secondary,
  }[colorMode];
  const [profilingModalIsOpen, setProfilingModalIsOpen] = useToggle(false);

  const { eventDistributor } = useContext(AuthContext);

  const { username } = useSelector(
    (state) => ({
      username: getC1UserDetails(state, 'name'),
    }),
    shallowEqual
  );

  const navigateApp = (routeName: string) => {
    eventDistributor.dispatch(actionDispatcher('NAVIGATE_APP', 'appName', routeName));
  };

  const navigateToConnector = (providerId: string) => {
    switch (providerId) {
      case 'aws':
        navigateApp('integrations/cloudprovideraccounts?open_connector=true#aws');
        break;
    }
  };

  interface ProviderTileProps {
    providerId: string;
    hasSentryAvailable?: boolean;
  }

  const ProviderTile = ({ providerId, hasSentryAvailable = false }: ProviderTileProps) => {
    return (
      <Flex
        direction="column"
        width={{ xs: '100%', md: '31%' }}
        padding="6x"
        marginBottom={{ xs: '4x', md: '0' }}
        backgroundColor={tileBG}
        boxShadow={colorStyle.shadow['medium']}
        data-id={`onboarding-provider-selection-${providerId}`}
        data-telemetry-id={`onboarding-provider-selection-${providerId}`}
      >
        <Stack justifyContent="center" height="220px">
          <Image
            alt={providerId + 'logo'}
            src={providerId === 'aws' ? awslogo : providerId === 'azure' ? azurelogo : gcplogo}
            maxWidth="200px"
            alignSelf="center"
          />
        </Stack>
        <Stack alignSelf="center" mb="2x" fontWeight="semibold" mt="4x">
          <Text size="xl" color={colorStyle.text.emphasis}>
            {t(`onboarding.cloudProviderSelection.providers.${providerId}.serviceName`)}
          </Text>
        </Stack>
        <Stack mt="4x" mb="8x">
          <Text>
            {t(`onboarding.cloudProviderSelection.providers.${providerId}.serviceDescription`)}
          </Text>
        </Stack>
        <Button
          onClick={() =>
            hasSentryAvailable ? navigateToConnector(providerId) : setProfilingModalIsOpen(true)
          }
          variant={hasSentryAvailable ? 'primary' : 'default'}
          width="100%"
          mt="auto"
          borderRadius="2rem"
          data-id={`onboarding-provider-selection-${providerId}-button`}
          data-telemetry-id={`onboarding-provider-selection-${providerId}-button`}
        >
          {t(`onboarding.cloudProviderSelection.providers.${providerId}.connectButtonText`)}
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <Stack
        minHeight="calc(100vh - 40px)"
        justifyContent="center"
        backgroundColor={pageBG}
        py="8x"
      >
        <Stack maxWidth={{ xs: '90%', xl: '1180px' }} alignSelf="center">
          <Text size="2xl">
            {t('onboarding.cloudProviderSelection.welcomeText', { username: username })}
          </Text>
          <Text mt="6x" size="4xl" color={colorStyle.text.emphasis}>
            {t('onboarding.cloudProviderSelection.pageTitle')}
          </Text>
          <Text mt="3x">{t('onboarding.cloudProviderSelection.pageSubTitle')}</Text>

          <Flex direction={{ xs: 'column', md: 'row' }} mt="8x" justify="space-between">
            <ProviderTile providerId="aws" hasSentryAvailable={true} />
            <ProviderTile providerId="azure" />
            <ProviderTile providerId="gcp" />
          </Flex>

          <Flex mt="8x" justify="center">
            <RouteLink id="onboarding-provider-selection-go-home" href="/home" changeApp={true}>
              {t('onboarding.cloudProviderSelection.goToHomeLinkText')}
            </RouteLink>
          </Flex>
        </Stack>
      </Stack>

      <ProfilingModal
        isOpen={profilingModalIsOpen}
        onClose={() => setProfilingModalIsOpen(false)}
      />
    </>
  );
};

export default Onboarding;
